/**
 * Helper utility for View Transitions API
 * Provides a consistent way to handle view transitions with fallback for unsupported browsers
 */

/**
 * Executes a state update with View Transition if supported, otherwise falls back to immediate execution
 * @param updateFn - Function that updates the state/DOM
 * @returns Promise that resolves when transition is complete
 */
export const withViewTransition = (updateFn: () => void): Promise<void> => {
	// @ts-ignore -- startViewTransition fails for TS
	if (document.startViewTransition) {
		// @ts-ignore -- startViewTransition fails for TS
		return document.startViewTransition(updateFn).finished;
	}
	// Fallback for browsers without View Transition support
	updateFn();
	return Promise.resolve();
};

/**
 * Creates a state setter function that automatically wraps updates in View Transitions
 * @param setState - React setState function
 * @returns Function that sets state with View Transition
 */
export const createViewTransitionSetter =
	<T>(setState: (value: T) => void) =>
	(value: T): Promise<void> =>
		withViewTransition(() => setState(value));

/**
 * Hook-like helper for creating transition-aware state setters
 * Usage: const setModalStateWithTransition = useViewTransitionSetter(setModalState);
 */
export const useViewTransitionSetter = <T>(setState: (value: T) => void) => createViewTransitionSetter(setState);
